<template>
	<div class="thread-view" ref="threadViewContainer">
		<DropZone v-if="isCreationMode && !isCreating" scoped @drop="onDropFiles" />

		<!-- Creation/Creating header (simple, no menus) -->
		<div v-if="isCreationMode || isCreating" class="thread-view-header">
			<div class="thread-icon-wrapper">
				<ReelIcon type="solid" />
			</div>
			<div class="thread-title">{{ $t('chat_thread_new_title') }}</div>
			<CloseIcon type="regular" class="pointer" @click="$emit('close')" />
		</div>

		<!-- Normal mode header (shared component with menus) -->
		<ThreadViewHeader
			v-else
			:chatId="effectiveChatId"
			sectionName="thread_chat-header"
			showClose
			@close="$emit('close')"
		/>

		<!-- CREATION MODE -->
		<template v-if="isCreationMode && !isCreating">
			<div class="thread-creation-content">
				<div class="thread-creation-body">
					<div class="creation-icon">
						<ReelIcon type="solid" />
					</div>
					<FormItem
						:label="threadNameLabel"
						:error="titleError"
						:message="$t('chat_thread_name_required_error')"
						class="creation-name-field"
					>
						<Input
							v-model="threadName"
							ref="threadNameInput"
							:placeholder="defaultThreadName"
							class="creation-name-input"
							@keydown.tab.prevent="$refs.messageEditor?.onFocus?.()"
						/>
					</FormItem>
				</div>

				<MessagePreview
					v-if="thread.source_message"
					:message="thread.source_message"
					class="thread-source-message"
				/>
			</div>

			<div class="thread-creation-footer">
				<MediaPreview
					v-if="mediaItems.length"
					class="thread-media-preview"
					:mediaItems="mediaItems"
					animate
					@remove="removeMediaItem"
					@reorder="reorderMediaItems"
					@click="onClickMediaItem"
				/>
				<div class="creation-input-row">
					<Editor
						v-model="firstMessage"
						ref="messageEditor"
						class="creation-message-editor"
						:placeholder="$t('chat_input_text_placeholder')"
						:disabled="isCreating"
						noClear
						noDraft
						@enterPressed="submitNewThread"
					/>
					<div class="creation-actions">
						<GiphyPicker ref="giphyPicker" @change="onGif" />
						<input
							:id="fileInputId"
							type="file"
							ref="fileInput"
							multiple
							class="hidden-file-input"
							@change="onSelectFiles"
							@click="handleFileChange"
						/>
						<Menu
							@click.stop.prevent
							:items="menuItems"
							bottom
							ref="actionsMenu"
							:offsetY="-8"
							@select="onMenuItemSelect"
							menuClass="thread-creation-menu"
						>
							<div class="actions-trigger">
								<CirclePlusIcon class="plus-icon pointer" type="regular" />
							</div>

							<template #item="{ item }">
								<div class="option-item">
									<div class="item-label">{{ item.label }}</div>
									<component class="item-icon" :is="item.icon" type="regular" />
								</div>
							</template>
						</Menu>
					</div>
				</div>
			</div>
		</template>

		<!-- CREATING STATE -->
		<div v-else-if="isCreating" class="thread-creating-state">
			<div class="spinner-wrapper">
				<IosSpinner background="transparent" />
			</div>
			<div class="creating-label">{{ $t('chat_thread_creating') }}</div>
		</div>

		<!-- NORMAL MODE -->
		<ChatPanel
			v-else
			:chatId="effectiveChatId"
			:mentionChatId="thread.parentChatId || thread.parent_chat_id"
			subscriptionType="thread"
			:hideCreateThread="true"
			idPrefix="thread_"
			:placeholderOverride="$t('chat_input_text_placeholder')"
			:compact="true"
			@loaded="onLoaded"
		/>
	</div>
</template>

<script>
import {
	CloseIcon,
	ReelIcon,
	CirclePlusIcon,
	GifIcon,
	PaperclipVerticalIcon,
} from '@/icons'
import {
	Input,
	Menu,
	GiphyPicker,
	showAlertModal,
	FormItem,
} from '@/components/common'
import Editor from '@/components/Editor'
import IosSpinner from '@/components/IosSpinner'
import ChatPanel from './ChatPanel.vue'
import ThreadViewHeader from './ThreadViewHeader.vue'
import MediaPreview from '@/components/MessageInput/MediaPreview'
import DropZone from '@/components/Home/DropZone'
import { createThread } from '@/api/thread'
import { generateThreadTitle } from '@/helpers/utils'
import MessagePreview from '@/components/Message/MessagePreview'
import { sendMessage, prepFilesForUpload } from '@/api/message'
import store, { getMyUserId, setMessage, getChat } from '@/store'
import { setUpload } from '@/store/uploads'
import {
	normalizeGiphyMediaItem,
	getImageFilesFromClipboard,
} from '@/helpers/utils'
import { MAX_MESSAGE_MEDIA } from '@/helpers/constants'
import moment from 'moment'

export default {
	name: 'ThreadView',

	components: {
		CloseIcon,
		ReelIcon,
		CirclePlusIcon,
		GifIcon,
		PaperclipVerticalIcon,
		Input,
		Menu,
		GiphyPicker,
		Editor,
		IosSpinner,
		ChatPanel,
		ThreadViewHeader,
		MediaPreview,
		FormItem,
		DropZone,
		MessagePreview,
	},

	props: {
		thread: {
			type: Object,
			required: true,
		},
	},

	emits: ['close', 'thread-created'],

	data() {
		return {
			threadName: '',
			firstMessage: '',
			isCreating: false,
			createdThread: null,
			mediaItems: [],
			titleError: false,
			tempUUID: crypto.randomUUID(),
		}
	},

	mounted() {
		if (this.isCreationMode) {
			this.$nextTick(() => {
				if (this.thread?.source_message) {
					this.$refs.messageEditor?.onFocus?.()
				} else {
					this.$refs.threadNameInput?.focus?.()
				}
			})
			window.addEventListener('paste', this.onPasteFiles)
			this.$onEventBus('paste-files-to-thread', ({ files }) => {
				this.onFileChange(files)
			})
			this._pasteListenersRegistered = true
		}
	},

	beforeUnmount() {
		if (this._pasteListenersRegistered) {
			window.removeEventListener('paste', this.onPasteFiles)
		}
	},

	computed: {
		isCreationMode() {
			return !this.thread?.id && !this.createdThread
		},
		hasSourceMessage() {
			return !!this.thread?.source_message
		},
		threadNameLabel() {
			return this.hasSourceMessage
				? this.$t('chat_thread_name_label_optional')
				: this.$t('chat_thread_name_label')
		},
		effectiveChatId() {
			return this.createdThread?.id || this.thread?.id || 0
		},
		defaultThreadName() {
			if (this.thread?.source_message) {
				return (
					generateThreadTitle(this.thread.source_message.message) ||
					this.$t('chat_thread_default_title')
				)
			}
			return this.$t('chat_thread_new_title')
		},
		parentChat() {
			return getChat(this.thread?.parentChatId) || {}
		},
		fileInputId() {
			return `thread-file-input-${this.thread?.parentChatId || 'new'}`
		},
		menuItems() {
			return [
				{
					id: 'gif',
					label: this.$t('common_menu_gif'),
					icon: GifIcon,
				},
				{
					id: 'file',
					label: this.$t('common_menu_file'),
					icon: PaperclipVerticalIcon,
				},
			]
		},
	},

	watch: {
		threadName(val) {
			if (val.trim()) this.titleError = false
		},
		thread(newVal) {
			// Reset creation state when thread prop changes to a new creation request
			if (!newVal?.id) {
				this.createdThread = null
				this.threadName = ''
				this.firstMessage = ''
				this.mediaItems = []
				this.isCreating = false
				this.titleError = false
				this.tempUUID = crypto.randomUUID()

				this.$nextTick(() => {
					if (newVal?.source_message) {
						this.$refs.messageEditor?.onFocus?.()
					} else {
						this.$refs.threadNameInput?.focus?.()
					}
				})
			}
		},
	},

	methods: {
		onLoaded() {
			// Thread-specific load handling if needed
		},

		onPasteFiles(e) {
			if (!this.isCreationMode) return
			if (!this.$refs.threadViewContainer?.contains(document.activeElement))
				return
			const files = getImageFilesFromClipboard(e)
			if (!files.length) return
			e.__pasteHandled = true
			this.onFileChange(files)
		},

		/**
		 * ARCHITECTURE NOTE: Why ThreadView duplicates editor/media code instead of reusing MessageInput
		 *
		 * MessageInput has tight coupling to getCurrentChat() that ignores chatIdOverride:
		 * - disabled() and placeholder() check permissions on wrong chat
		 * - store.currentChatId watcher destructively clears mediaItems
		 * - Features like edit-last-message don't apply in creation mode
		 *
		 * Refactoring MessageInput to work here would require 5+ conditional props and
		 * risk subtle bugs. The duplication keeps ThreadView's creation logic self-contained.
		 *
		 * Future option: Extract EditorWithMedia component if more places need this pattern.
		 */
		onMenuItemSelect(item) {
			switch (item.id) {
				case 'gif':
					this.$refs.giphyPicker.showGiphy()
					break
				case 'file':
					this.$refs.fileInput.click()
					break
			}
		},

		handleFileChange(e) {
			e.target.value = ''
		},

		onSelectFiles(e) {
			this.onFileChange(e.target.files)
		},

		async onFileChange(files = []) {
			if (!files?.length) return false

			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const normalizedMedia = await prepFilesForUpload(
				files,
				this.mediaItems.length,
				this.tempUUID
			)

			normalizedMedia.forEach((media) => {
				this.mediaItems.push(media)
			})

			this.$nextTick(() => this.$refs.messageEditor?.onFocus?.())
		},

		onGif(giphyData) {
			if (!giphyData || !giphyData.images) return
			if (this.mediaItems.length >= MAX_MESSAGE_MEDIA) {
				return showAlertModal({
					title: this.$t('chat_attachment_max_limit_exceeded_title'),
					description: this.$t(
						'chat_attachment_max_limit_exceeded_description',
						{ Count: MAX_MESSAGE_MEDIA }
					),
				})
			}
			if (!this.tempUUID) {
				this.tempUUID = crypto.randomUUID()
			}
			const mediaItem = {
				...normalizeGiphyMediaItem(giphyData),
				isComplete: true,
				isNormalized: true,
				messageUUID: this.tempUUID,
			}
			delete mediaItem.uuid
			setUpload(mediaItem.tempId, {
				tempId: mediaItem.tempId,
				mediatype: mediaItem.mediatype,
				messageUUID: mediaItem.messageUUID,
				uploadProgress: 100,
				isComplete: true,
				isNormalized: true,
			})
			this.mediaItems.push(mediaItem)
			this.$nextTick(() => this.$refs.messageEditor?.onFocus?.())
		},

		removeMediaItem(item) {
			const index = this.mediaItems.findIndex((m) => m.tempId === item.tempId)
			if (index !== -1) {
				this.mediaItems.splice(index, 1)
			}
		},

		reorderMediaItems(orderedTempIds) {
			if (!this.mediaItems?.length) return
			this.mediaItems = orderedTempIds.map((tempId) =>
				this.mediaItems.find((m) => m.tempId === tempId)
			)
		},

		onClickMediaItem(item, items) {
			this.$mediaItems(items, item.tempId)
		},

		onDropFiles(e) {
			const { files } = e.dataTransfer || e.target || {}
			this.onFileChange(files)
		},

		async submitNewThread() {
			// Validate - need either message or media
			const hasText = this.firstMessage.trim().length > 0
			const hasMedia = this.mediaItems.length > 0
			if (!hasText && !hasMedia) return

			// Title is required when creating from scratch (no source message)
			if (!this.hasSourceMessage && !this.threadName.trim()) {
				this.titleError = true
				this.$refs.threadNameInput?.focus?.()
				return
			}

			if (this.isCreating) return

			this.isCreating = true

			try {
				// 1. Create the thread
				const newThread = await createThread(this.thread.parentChatId, {
					title: this.threadName.trim() || this.defaultThreadName,
					source_message_id: this.thread.source_message?.id,
				})

				// 2. Store created thread to transition UI
				this.createdThread = newThread

				// 3. Send the first message (failure here is non-fatal — thread already exists)
				try {
					await this.sendFirstMessage(newThread.id)
				} catch (e) {
					console.error('Failed to send first message in thread:', e)
				}

				// 4. Notify parent
				this.$emit('thread-created', newThread)
			} catch (e) {
				console.error('Failed to create thread:', e)
				this.createdThread = null
			} finally {
				this.isCreating = false
			}
		},

		async sendFirstMessage(threadId) {
			const messagePayload = {
				id: null,
				uuid: this.tempUUID || crypto.randomUUID(),
				bubble_id: threadId,
				message: this.firstMessage.trim(),
				created_at: moment().utc().format('YYYY-MM-DD HH:mm:ss'),
				sendState: 'waiting',
				user_id: getMyUserId(),
				messagemedia: this.mediaItems.length > 0 ? this.mediaItems : [],
			}

			// Add to store first
			setMessage(messagePayload)

			// Then send
			await sendMessage(messagePayload)

			// Clear media items after sending
			this.mediaItems = []
			this.tempUUID = crypto.randomUUID()
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.thread-view {
	grid-area: thread;
	margin-left: 8px;
	border-radius: 8px 0 0 8px;
	border-left: 1px solid var(--neutral-40040);
	background: var(--neutral-0);
	@include flex-column;
	overflow: hidden;
	position: relative;

	.thread-view-header {
		@include flex-spread;
		height: 60px;
		padding: 0 16px;
		border-bottom: 1px solid var(--neutral-40040);
		flex-shrink: 0;
		background: var(--neutral-0);

		.thread-icon-wrapper {
			width: 32px;
			height: 32px;
			border-radius: 6px;
			background: var(--neutral-100);
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;

			.pronto-icon {
				color: var(--neutral-500);
				font-size: 20px;
				transform: rotate(30deg);
			}
		}

		.thread-title {
			flex: 1;
			font-family: Avenir-Heavy;
			font-size: 16px;
			color: var(--neutral-800);
			margin-left: 8px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.thread-header-actions {
			display: flex;
			align-items: center;

			.header-action {
				cursor: pointer;
				padding: 6px;
				border-radius: 6px;
				display: flex;
				align-items: center;
				justify-content: center;

				.pronto-icon {
					font-size: 18px;
					color: var(--neutral-400);

					&.bell-slash {
						font-size: 18px;
						transform: scale(1.22);
					}
				}

				&:hover .pronto-icon {
					color: var(--neutral-500);
				}
			}

			:deep(.actions) {
				.pronto-icon {
					color: var(--neutral-400);
				}
			}

			.header-dots-menu {
				cursor: pointer;
				padding: 6px;
				border-radius: 6px;
				display: flex;
				align-items: center;
				justify-content: center;

				.pronto-icon {
					font-size: 16px;
					color: var(--neutral-400);
				}

				&:hover .pronto-icon {
					color: var(--neutral-500);
				}
			}
		}

		> .pronto-icon {
			cursor: pointer;
			color: var(--neutral-400);
			font-size: 22px;

			&:hover {
				color: var(--neutral-500);
			}
		}
	}

	.thread-creation-content {
		flex: 1;
		overflow-y: auto;
		min-height: 0;
		display: flex;
		flex-direction: column;
	}

	.thread-creation-body {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		padding: 20px 16px 8px;
		flex: 1;
		justify-content: flex-end;

		.creation-icon {
			width: 50px;
			height: 50px;
			border-radius: 8px;
			background: var(--neutral-100);
			display: flex;
			align-items: center;
			justify-content: center;
			margin-bottom: 24px;

			.pronto-icon {
				font-size: 28px;
				color: var(--neutral-500);
				transform: rotate(30deg);
			}
		}

		.creation-name-field {
			width: 100%;

			:deep(.form-item-label) {
				font-family: Avenir-Medium;
				font-size: 16px;
				color: var(--neutral-800);
			}

			:deep(.form-item-footer) {
				min-height: 16px;

				.form-item-message {
					font-size: 12px;
					font-family: Avenir-Medium;
					color: var(--red-300);
				}
			}

			:deep(.form-item-alert) {
				color: var(--red-300);
			}

			:deep(.input) {
				padding: 8px;
			}
		}
	}

	.thread-source-message {
		padding-top: 8px;
		padding-bottom: 20px;
		min-width: 0;
		overflow-wrap: break-word;
		word-break: break-word;
	}

	.thread-creation-footer {
		display: flex;
		flex-direction: column;
		padding: 0 16px;
		border-top: 1px solid var(--neutral-40040);
		flex-shrink: 0;

		.creation-input-row {
			display: flex;
			align-items: flex-end;
			gap: 16px;
			min-height: 55px;
		}

		.creation-message-editor {
			flex: 1;
			--padding: 13px;
			--max-height: 120px;
			height: auto;
			min-height: 52px;
		}

		.creation-actions {
			@include flex;
			margin-bottom: 15px;

			.actions-trigger {
				display: flex;
				align-items: center;
				justify-content: center;
			}

			.plus-icon {
				font-size: 24px;
				color: var(--neutral-400);

				&:hover {
					color: var(--neutral-600);
				}
			}
		}
	}

	.thread-creating-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		flex: 1;
		gap: 16px;

		.spinner-wrapper {
			position: relative;
			width: 70px;
			height: 70px;
		}

		.creating-label {
			font-family: Avenir-Medium;
			font-size: 14px;
			color: var(--neutral-500);
		}
	}

	:deep(.chat-panel) {
		flex: 1;
		overflow: hidden;
	}

	.thread-media-preview {
		padding: 8px 0;
	}

	.hidden-file-input {
		display: none;
	}
}

// Thread overlay mode for narrower screens
@media screen and (max-width: 1100px) {
	.thread-view {
		grid-area: unset;
		position: fixed;
		right: 0;
		top: 0;
		bottom: 0;
		width: 408px;
		max-width: 100vw;
		z-index: 101;
		margin-left: 0;
		border-radius: 8px 0 0 8px;
		box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
		animation: slideInFromRight 0.3s ease;
	}
}

@media screen and (max-width: 500px) {
	.thread-view {
		width: 100vw;
		border-radius: 0;
	}
}

@keyframes slideInFromRight {
	from {
		transform: translateX(100%);
		opacity: 0;
	}
	to {
		transform: translateX(0);
		opacity: 1;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.thread-creation-menu {
	.menu-item {
		min-width: 140px !important;
		padding: 4px 4px !important;
	}

	.option-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 4px 8px;

		.item-label {
			font-family: Avenir-Medium;
			font-size: 14px;
			color: var(--neutral-700);
		}

		.item-icon {
			font-size: 18px;
			color: var(--neutral-500);
		}
	}
}

.thread-notification-menu {
	padding-top: 8px;
	padding-bottom: 8px;
	.menu-item {
		min-width: 160px !important;
		padding: 8px 16px !important;
	}

	.notification-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: 16px;

		.notification-label {
			font-family: Avenir-Medium;
			font-size: 14px;
		}

		.notification-spinner {
			font-size: 16px;
		}

		.notification-check {
			color: var(--teal-300);
			font-size: 16px;
		}

		.notification-circle {
			color: var(--neutral-300);
			font-size: 16px;
		}

		&.mute {
			border-top: 1px solid var(--neutral-200);
			padding-top: 12px;

			.mute-chevron {
				font-size: 12px;
				color: var(--neutral-400);
			}
		}
	}
}

.menu-item.divider-above {
	margin-top: 4px;
	padding-top: 8px;
	background-image: linear-gradient(var(--neutral-200), var(--neutral-200));
	background-size: calc(100% - 32px) 1px;
	background-position: center top;
	background-repeat: no-repeat;
}

.thread-menu-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;

	&.danger {
		color: var(--red-200);
		padding-bottom: 4px;
	}

	&.divider-content {
		padding-top: 4px;
	}

	&:has(.thread-menu-item-subtitle) {
		padding-top: 4px;
	}

	.thread-menu-item-text {
		display: flex;
		flex-direction: column;

		.thread-menu-item-label {
			font-family: Avenir-Medium;
			font-size: 14px;
		}

		.thread-menu-item-subtitle {
			font-family: Avenir-Book;
			font-size: 12px;
			color: var(--neutral-500);
			margin-top: 1px;
		}
	}

	.thread-menu-spinner {
		font-size: 16px;
	}
}

@include overrideScheme('dark') {
	.thread-view {
		background: var(--neutral-100);

		.thread-view-header {
			background: var(--neutral-100);
		}

		.creation-icon {
			background: var(--neutral-0) !important;
		}

		.creation-name-input.pronto-input {
			background: var(--neutral-0) !important;
		}

		.thread-creation-footer {
			background: var(--neutral-0);
			border-top-color: var(--neutral-300);

			.creation-message-input {
				color: var(--neutral-800);
			}
		}
	}
}
</style>
